import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { regimenesFiscales, stepTwoSchema, usosCFDI } from '../../schemas/facturas';

/**
 * Componente para el paso 2 del wizard de facturas
 * Solicita los datos fiscales para completar el CFDI
 */
const StepDatos = ({ onSubmit, onBack, initialValues = {} }) => {
  const { register, handleSubmit, formState: { errors, isValid } } = useForm({
    resolver: zodResolver(stepTwoSchema),
    mode: 'onChange',
    defaultValues: {
      nombreRazonSocial: initialValues.nombreRazonSocial || '',
      codigoPostal: initialValues.codigoPostal || '',
      regimenFiscal: initialValues.regimenFiscal || '',
      usoCfdi: initialValues.usoCfdi || '',
      email: initialValues.email || ''
    }
  });

  // Manejar el envío del formulario
  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 md:p-8">
      <h2 className="text-2xl font-bold text-primary mb-6 text-center">
        Datos de facturación
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
        {/* Nombre o Razón Social */}
        <div>
          <label htmlFor="nombreRazonSocial" className="block text-sm font-medium text-gray-700 mb-1">
            Nombre o Razón Social
          </label>
          <input
            id="nombreRazonSocial"
            type="text"
            {...register('nombreRazonSocial')}
            placeholder="Nombre completo o razón social"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.nombreRazonSocial ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-primary/20 focus:border-primary'
            }`}
          />
          {errors.nombreRazonSocial && (
            <p className="mt-1 text-sm text-red-600">{errors.nombreRazonSocial.message}</p>
          )}
        </div>

        {/* Código Postal */}
        <div>
          <label htmlFor="codigoPostal" className="block text-sm font-medium text-gray-700 mb-1">
            Código Postal
          </label>
          <input
            id="codigoPostal"
            type="text"
            {...register('codigoPostal')}
            placeholder="Ejemplo: 64000"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.codigoPostal ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-primary/20 focus:border-primary'
            }`}
            maxLength={5}
          />
          {errors.codigoPostal && (
            <p className="mt-1 text-sm text-red-600">{errors.codigoPostal.message}</p>
          )}
        </div>

        {/* Régimen Fiscal */}
        <div>
          <label htmlFor="regimenFiscal" className="block text-sm font-medium text-gray-700 mb-1">
            Régimen Fiscal
          </label>
          <select
            id="regimenFiscal"
            {...register('regimenFiscal')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.regimenFiscal ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-primary/20 focus:border-primary'
            }`}
          >
            <option value="">Selecciona una opción</option>
            {regimenesFiscales.map((regimen) => (
              <option key={regimen.value} value={regimen.value}>
                {regimen.label}
              </option>
            ))}
          </select>
          {errors.regimenFiscal && (
            <p className="mt-1 text-sm text-red-600">{errors.regimenFiscal.message}</p>
          )}
        </div>

        {/* Uso CFDI */}
        <div>
          <label htmlFor="usoCfdi" className="block text-sm font-medium text-gray-700 mb-1">
            Uso CFDI
          </label>
          <select
            id="usoCfdi"
            {...register('usoCfdi')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.usoCfdi ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-primary/20 focus:border-primary'
            }`}
          >
            <option value="">Selecciona una opción</option>
            {usosCFDI.map((uso) => (
              <option key={uso.value} value={uso.value}>
                {uso.label}
              </option>
            ))}
          </select>
          {errors.usoCfdi && (
            <p className="mt-1 text-sm text-red-600">{errors.usoCfdi.message}</p>
          )}
        </div>

        {/* Correo Electrónico */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Correo Electrónico
          </label>
          <input
            id="email"
            type="email"
            {...register('email')}
            placeholder="ejemplo@correo.com"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-primary/20 focus:border-primary'
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Botones */}
        <div className="flex space-x-4 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
          >
            Atrás
          </button>
          <button
            type="submit"
            disabled={!isValid}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all duration-300 ${
              isValid
                ? 'bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Facturar
          </button>
        </div>
      </form>
    </div>
  );
};

export default StepDatos;
